import { CalendarWrapperBox } from '@/components/CalenderWrapperBox.styled'
import DateField from '@/components/DateField'
import { PopoverContainer } from '@/components/Popover'
import SearchFormButton from '@/components/SearchFormButton'
import ShadowEffect from '@/components/ShadowEffect'
import StyledHeading from '@/components/StyledHeading'
import { fireInfantErrorEvent } from '@/components/TravelerSelection/ga4'
import TypeAhead from '@/components/TypeAhead'
import { PKG_SEARCH_SESSION_RECOVERY_KEY } from '@/constants/pkg-search-recovery'
import type { GA4SearchRedirectErrorDisplay } from '@/ga4types'
import useBootstrapData from '@/hooks/useBootstrapData'
import useContentfulSearchFormHeadlines from '@/hooks/useContentfulSearchFormHeadlines'
import useIsIPad from '@/hooks/useIsIPad'
import useSeti from '@/hooks/useSeti'
import useTabGroup from '@/hooks/useTabGroup'
import analytics from '@/shared-utils/analytics'
import { buildVacationPackagesFormFromTripsInitialValues } from '@/shared-utils/build-vacation-packages-form-from-trips-initial-values'
import {
  addFormattedDays,
  defaultMaxDate,
  defaultToday,
  formatDate,
  getPackagesDateRange,
  getRcMinDate,
  getTimeOptions,
} from '@/shared-utils/date-helper'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import { shouldApplyPkgRecoveryForXsell } from '@/shared-utils/pkg-session-recovery'
import { swap } from '@/shared-utils/ramda-helper'
import { Calendar } from '@pcln/date-picker'
import pclnExperimentation from '@pcln/experimentation'
import TravelerSelection, { type ITravelerInfo } from '@pcln/traveler-selection'
import { useRouter } from 'next/router'
import { Box, Checkbox, Flex, FormField, Label, type SelectProps } from 'pcln-design-system'
import { useRef, useState, type ChangeEvent } from 'react'
import { Controller } from 'react-hook-form'
import { IMPRESSION_ELIGIBLE_ERRORS } from '../errorTypes'
import type { PackagesFormStateType } from '../types'
import AdvancedSearchLink from './AdvancedSearchLink'
import { FlexLabel, StyledSelect, StyledTSBox } from './Form.styled'
import FormContainer from './FormContainer'
import GenericErrorBanner from './GenericErrorBanner'
import TripTypeRadios from './TripTypeRadios'
import {
  TripTypeInfos,
  hasFlight,
  hasHotel,
  isHotelFlight,
  isHotelFlightCar,
  isHotelPlusCar,
} from './TripTypes'
import { CALENDAR_POPOVER_SELECTOR, fields, getTextString, initialState } from './utils'
import { isInfantLimitReachedPackages, isInfantOnInternationalTravel, searchFormSchema } from './validation'

interface TimeFieldProps extends SelectProps {
  label: string
}

function TimeField({ name, label, ...props }: Readonly<TimeFieldProps>) {
  return (
    <FormField>
      <Label>{label}</Label>
      <StyledSelect {...props} id={name} name={name}>
        {getTimeOptions()}
      </StyledSelect>
    </FormField>
  )
}

type FormProps = {
  onSubmit: (pageName: 'landing' | 'homepage', data: PackagesFormStateType) => void
  pageName: 'landing' | 'homepage'
}

function Form({ onSubmit, pageName }: Readonly<FormProps>) {
  const { registerTabElement, focusNextElement } = useTabGroup()
  const {
    isMobile,
    prePopData,
    seti,
    webstats: { clientGUID: cguid },
  } = useBootstrapData()
  const {
    tripType,
    header,
    startDate: prePopStartDate,
    endDate: prePopEndDate,
    startLocation,
    endLocation,
    adultCount,
    childrenCount,
    infantCount,
    roomCount,
  } = prePopData?.packages ?? {}
  const errorFired = useRef(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [isRcPickupCalendarOpen, setIsRcPickupCalendarOpen] = useState(false)
  const [isRcDropoffCalendarOpen, setIsRcDropoffCalendarOpen] = useState(false)
  const [isHotelCalendarOpen, setIsHotelCalendarOpen] = useState(false)

  const primaryHeadingText = 'Save up to $625 on your '

  const { query } = useRouter()
  const shouldShowErrorBanner =
    pageName === 'landing' &&
    typeof query['redirect-source'] === 'string' &&
    query['redirect-source'].toLowerCase() === 'error'
  const errorType = typeof query['message'] === 'string' ? query['message'] : undefined
  const shouldFireSpecificErrorImpression =
    shouldShowErrorBanner &&
    !!errorType &&
    (IMPRESSION_ELIGIBLE_ERRORS as readonly string[]).includes(errorType)
  const specificErrorCopySeti = useSeti({
    experimentCode: 'PKG_SPECIFIC_ERROR_COPY',
    shouldFireImpression: shouldFireSpecificErrorImpression,
  })

  const searchButtonTextCopySeti = useSeti({ experimentCode: 'PKG_TEST_SEARCH_BUTTON_COPY' })
  const flyTypeaheadSeti = useSeti({ experimentCode: 'PKG_AC_FLY_TYPEAHEAD', shouldFireImpression: false })

  const buttonText = searchButtonTextCopySeti === 'VARIANT' ? 'Search' : 'Find Your Trip'

  const prePopInitialState = {
    ...initialState,
    ...(tripType && { tripType }),
    ...(prePopStartDate && { startDate: prePopStartDate }),
    ...(prePopEndDate && { endDate: prePopEndDate }),
    ...(startLocation && { startLocation }),
    ...(endLocation && { endLocation }),
    travelers: {
      adults: adultCount || 2,
      children: childrenCount || 0,
      lapInfants: infantCount || 0,
      childrenAges: {},
      rooms: roomCount || 1,
    },
  }

  const searchForm = useContentfulSearchFormHeadlines('searchForm')
  const defaultTripTypes = Object.values(TripTypeInfos)
  const mobileDefaultTripTypes = swap(1, 2, defaultTripTypes)
  const tripTypes = isMobile ? mobileDefaultTripTypes : defaultTripTypes
  const maxDaysHotelToSelect = 30

  const isIPad = useIsIPad()
  return (
    <FormContainer
      onSubmit={(data: PackagesFormStateType) => {
        if (data.tripType === 'FLY:DRIVE') {
          pclnExperimentation.fireImpression('PKG_AC_FLY_TYPEAHEAD')
        }
        onSubmit(pageName, data)
        logSearchFormAnalytics({ cguid, pageName, productType: 'vacations', isMobile })
      }}
      fields={fields}
      initialState={prePopInitialState}
      searchFormSchema={searchFormSchema}
      onReady={
        shouldShowErrorBanner
          ? (setValue) => {
              try {
                const sessionRaw = sessionStorage.getItem(PKG_SEARCH_SESSION_RECOVERY_KEY)
                if (!sessionRaw) return

                const wrapper = JSON.parse(sessionRaw) as {
                  isAdvancedSearch?: boolean
                  containsXSell?: boolean
                  tripsSearchForm?: Record<string, unknown>
                }
                if (!shouldApplyPkgRecoveryForXsell(wrapper, seti)) {
                  sessionStorage.removeItem(PKG_SEARCH_SESSION_RECOVERY_KEY)
                  return
                }
                if (
                  wrapper.isAdvancedSearch !== false ||
                  !wrapper.tripsSearchForm ||
                  typeof wrapper.tripsSearchForm !== 'object'
                ) {
                  return
                }

                const parsed: Partial<PackagesFormStateType> =
                  buildVacationPackagesFormFromTripsInitialValues(wrapper.tripsSearchForm)
                sessionStorage.removeItem(PKG_SEARCH_SESSION_RECOVERY_KEY)

                if (parsed.travelers) {
                  const t = parsed.travelers
                  parsed.travelers = {
                    adults: Number(t.adults) || 2,
                    children: Array.isArray(t.children) ? t.children.length : Number(t.children) || 0,
                    lapInfants: Number(t.lapInfants) || 0,
                    rooms: Number(t.rooms) || 1,
                    childrenAges: t.childrenAges && typeof t.childrenAges === 'object' ? t.childrenAges : {},
                  }
                }

                ;(Object.keys(parsed) as Array<keyof PackagesFormStateType>).forEach((key) => {
                  const value = parsed[key]
                  if (value !== undefined) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setValue(key, value as any, { shouldDirty: true })
                  }
                })
                const ga4Event: GA4SearchRedirectErrorDisplay = {
                  event: 'display',
                  attributes: {
                    type: 'pkg_landing_search_redirect_error',
                    item_name: 'search_data_persisted',
                    page_name: 'landing_page',
                    product_name: 'bundle',
                  },
                }
                analytics.fireGA4Event(ga4Event)
              } catch {
                // noop — storage unavailable or malformed data
              }
            }
          : undefined
      }
    >
      {({ setValue, errors, register, control, watchState }) => {
        const {
          watchTripType,
          watchTravelers,
          watchHotelStartDate,
          watchHotelEndDate,
          watchStartDate,
          watchEndDate,
          watchPartialStay,
          watchStartLocation,
          watchEndLocation,
          watchPickupCarSomewhere,
          watchRcLocation,
          watchRcPickupDate,
          watchRcDropoffDate,
          watchRcPickupTime,
          watchRcDropoffTime,
        } = watchState
        const allowSameDaySearch = watchTripType === 'FLY:DRIVE'
        const finalPrimaryHeadingText = getTextString(watchTripType)(primaryHeadingText)
        const contentfulHeadline = isMobile ? searchForm?.mobileHeadline : searchForm?.headline
        const headline = header
          ? `Save big on bundles to ${header}`
          : contentfulHeadline || finalPrimaryHeadingText

        const carLocationLabel = 'City, Airport or Address'

        const maxDaysToSelect = !hasHotel(watchTripType) ? 330 : maxDaysHotelToSelect
        if (
          (isInfantOnInternationalTravel(
            watchStartLocation,
            watchEndLocation,
            watchTravelers.childrenAges,
            watchTripType,
          ) ||
            isInfantLimitReachedPackages(
              watchTravelers.adults,
              watchTravelers.childrenAges,
              watchTripType,
            )) &&
          !errorFired.current
        ) {
          errorFired.current = true
          fireInfantErrorEvent(pageName, 'bundle')
        }
        const showPartialStay = isHotelFlightCar(watchTripType) || isHotelFlight(watchTripType)
        return (
          <>
            {pageName === 'landing' && (
              <>
                <StyledHeading
                  pb={1}
                  tag='h1'
                  color='text.heading'
                  text={headline}
                  textStyle={['heading3', null, null, null, 'heading2']}
                />
                {shouldShowErrorBanner && (
                  <div className='mt-2 mb-3'>
                    <GenericErrorBanner
                      errorType={specificErrorCopySeti === 'VARIANT' ? errorType : undefined}
                    />
                  </div>
                )}
              </>
            )}

            <Flex flexDirection='column' flexWrap='nowrap' style={{ gap: '16px' }}>
              <TripTypeRadios
                tripTypes={tripTypes}
                watchTripType={watchTripType}
                onChange={(selectedTripType) => {
                  setValue('tripType', selectedTripType, { shouldDirty: true })
                }}
              />
              <Flex flexWrap='wrap' style={{ gap: '16px' }}>
                {!isHotelPlusCar(watchTripType) && (
                  <Box style={{ flex: '1 1 245px' }}>
                    <ShadowEffect enabled>
                      {({ disableShadowState }: { disableShadowState: () => void }) => (
                        <TypeAhead
                          searchProduct='packagesOrigin'
                          label='Where from?'
                          placeholder='Where from?'
                          searchKey='startLocation'
                          setValue={setValue}
                          errors={errors}
                          disableShadowState={disableShadowState}
                          defaultSelectedItem={watchStartLocation}
                          ref={registerTabElement}
                          onItemSelect={() => {
                            if (isMobile && document.activeElement instanceof HTMLElement) {
                              document.activeElement.blur()
                            }
                          }}
                          focusNextElement={focusNextElement}
                          skipBottomPaddingForMobile
                        />
                      )}
                    </ShadowEffect>
                  </Box>
                )}
                <Box style={{ flex: '1 1 245px' }}>
                  <ShadowEffect enabled>
                    {({ disableShadowState }: { disableShadowState: () => void }) => (
                      <TypeAhead
                        searchProduct={
                          flyTypeaheadSeti === 'VARIANT' && watchTripType === 'FLY:DRIVE'
                            ? 'flights'
                            : 'hotels'
                        }
                        label='Where to?'
                        placeholder='Where to?'
                        searchKey='endLocation'
                        errors={errors}
                        setValue={setValue}
                        disableShadowState={disableShadowState}
                        defaultSelectedItem={watchEndLocation}
                        onItemSelect={(item) => {
                          // auto-populate RC location if not set yet
                          if (!watchRcLocation) {
                            setValue('rcLocation', item, { shouldDirty: true })
                          }
                          if (isMobile && document.activeElement instanceof HTMLElement) {
                            document.activeElement.blur()
                          }
                        }}
                        ref={registerTabElement}
                        focusNextElement={focusNextElement}
                        skipBottomPaddingForMobile
                      />
                    )}
                  </ShadowEffect>
                </Box>
              </Flex>
              <AdvancedSearchLink pageName={pageName} watchState={watchState} />
              <Flex flexWrap='wrap' style={{ gap: '16px' }}>
                <CalendarWrapperBox style={{ flex: '1 1 245px' }}>
                  <ShadowEffect enabled>
                    {({ disableShadowState }: { disableShadowState: () => void }) => (
                      <PopoverContainer
                        data-calendar
                        open={isCalendarOpen}
                        onDismiss={() => {
                          setIsCalendarOpen(false)
                          disableShadowState()
                        }}
                        domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                        skipBottomPaddingForMobile
                      >
                        <DateField
                          name='travelDates'
                          index={1}
                          label={
                            isHotelPlusCar(watchTripType)
                              ? 'Check-in   -   Check-out'
                              : 'Departing - Returning'
                          }
                          ref={registerTabElement}
                          value={getPackagesDateRange({
                            startDate: watchStartDate,
                            endDate: watchEndDate,
                          })}
                          onFocus={() => {
                            setIsCalendarOpen(true)
                          }}
                          error={(() => {
                            if (errors.startDate) {
                              return errors.startDate.message
                            }
                            if (errors.endDate) {
                              return errors.endDate.message
                            }
                          })()}
                          aria-expanded={isCalendarOpen ? 'true' : 'false'}
                        />

                        <Calendar
                          allowSameDay={allowSameDaySearch}
                          use2024DesignSeti
                          isMobile={isMobile}
                          roundedCorners
                          monthWidthPx={350}
                          startDate={watchStartDate}
                          endDate={watchEndDate}
                          maxDaysToSelect={maxDaysToSelect}
                          onChange={(_, { startDate, endDate }) => {
                            if (startDate && endDate) {
                              setIsCalendarOpen(false)
                              disableShadowState()
                            }
                            setValue('startDate', startDate ?? '', {
                              shouldDirty: true,
                            })
                            setValue('endDate', endDate ?? '', {
                              shouldDirty: true,
                            })
                            setValue('hotelStartDate', startDate ?? '', {
                              shouldDirty: true,
                            })
                            setValue('hotelEndDate', endDate ?? '', {
                              shouldDirty: true,
                            })

                            // auto-populate RC dates if not set yet
                            if (!watchRcPickupDate)
                              setValue('rcPickupDate', startDate, {
                                shouldDirty: true,
                              })
                            if (!watchRcDropoffDate)
                              setValue('rcDropoffDate', endDate, {
                                shouldDirty: true,
                              })
                          }}
                          useDefaultFooter
                          onDismiss={() => {
                            setIsCalendarOpen(false)
                            disableShadowState()
                          }}
                        />
                      </PopoverContainer>
                    )}
                  </ShadowEffect>
                </CalendarWrapperBox>
                <StyledTSBox tabIndex={0} style={{ flex: '1 1 245px' }}>
                  <Controller
                    name='travelers'
                    control={control}
                    defaultValue={watchTravelers}
                    render={({ field }) => {
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      const { ref, ...rest } = field
                      return (
                        <TravelerSelection
                          {...rest}
                          isMobile={isMobile}
                          trapFocus={!isIPad}
                          initialValues={field.value}
                          hasShadowEffect
                          maxTravelers={8}
                          maxChildren={6}
                          onChange={(updatedValues: ITravelerInfo) => {
                            field.onChange({
                              childrenAges: updatedValues.childrenAges,
                              children: updatedValues.children,
                              rooms: updatedValues.rooms,
                              adults: updatedValues.adults,
                              lapInfants: updatedValues.lapInfants,
                            })
                          }}
                          errorMessage={
                            (isInfantOnInternationalTravel(
                              watchStartLocation,
                              watchEndLocation,
                              watchTravelers.childrenAges,
                              watchTripType,
                            ) ||
                              isInfantLimitReachedPackages(
                                watchTravelers.adults,
                                watchTravelers.childrenAges,
                                watchTripType,
                              ) ||
                              errors?.travelers?.children?.message) ??
                            ''
                          }
                          roomsEnabled={hasHotel(watchTripType)}
                          ageSelectEnabled
                          isHotel={!hasFlight(watchTripType)}
                          disclaimer='You may only book a package for up to 8 travelers.'
                          height={56}
                        />
                      )
                    }}
                  />
                </StyledTSBox>
                {!isMobile && <SearchFormButton buttonText={buttonText} style={{ flex: '1 1 245px' }} />}
              </Flex>
              {showPartialStay && (
                <>
                  <FlexLabel ml={-1} nowrap fontSize={0}>
                    <Checkbox {...register('partialStay')} /> I only need a hotel for part of my stay
                  </FlexLabel>
                  {watchPartialStay && (
                    <Box width={[1, null, 0.5, null, null, 0.32]}>
                      <ShadowEffect enabled>
                        {({ disableShadowState }: { disableShadowState: () => void }) => (
                          <PopoverContainer
                            data-calendar
                            domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                            onDismiss={() => {
                              setIsHotelCalendarOpen(false)
                              disableShadowState()
                            }}
                            open={isHotelCalendarOpen}
                            skipBottomPaddingForMobile
                          >
                            <DateField
                              index={2}
                              aria-expanded={isHotelCalendarOpen ? 'true' : 'false'}
                              error={(() => {
                                if (errors.hotelStartDate) {
                                  return errors.hotelStartDate.message
                                }
                                if (errors.hotelEndDate) {
                                  return errors.hotelEndDate.message
                                }
                              })()}
                              label='Check-in - Check-out'
                              name='hotelDates'
                              onFocus={() => {
                                setIsHotelCalendarOpen(true)
                              }}
                              value={getPackagesDateRange({
                                startDate: watchHotelStartDate ?? null,
                                endDate: watchHotelEndDate ?? null,
                              })}
                            />
                            <Calendar
                              use2024DesignSeti
                              isMobile={isMobile}
                              startDate={watchHotelStartDate}
                              endDate={watchHotelEndDate}
                              maxDate={watchEndDate ? addFormattedDays(watchEndDate, 1) : defaultMaxDate}
                              minDate={watchStartDate || defaultToday}
                              maxDaysToSelect={maxDaysToSelect}
                              roundedCorners
                              monthWidthPx={350}
                              onChange={(_, { startDate, endDate }) => {
                                if (startDate && endDate) {
                                  setIsHotelCalendarOpen(false)
                                  disableShadowState()
                                }
                                setValue('hotelStartDate', startDate ?? '', {
                                  shouldDirty: true,
                                })
                                setValue('hotelEndDate', endDate ?? '', {
                                  shouldDirty: true,
                                })
                              }}
                              monthsToShow={watchStartDate && watchEndDate ? 1 : 2}
                              onDismiss={() => {
                                setIsHotelCalendarOpen(false)
                                disableShadowState()
                              }}
                              useDefaultFooter
                            />
                          </PopoverContainer>
                        )}
                      </ShadowEffect>
                    </Box>
                  )}
                </>
              )}
              {isHotelPlusCar(watchTripType) && (
                <>
                  <FlexLabel ml={-1} nowrap fontSize={0}>
                    <Checkbox {...register('pickupCarSomewhere')} />I want to pick up my car somewhere else
                  </FlexLabel>
                  {watchPickupCarSomewhere && (
                    <>
                      <ShadowEffect enabled>
                        {({ disableShadowState }: { disableShadowState: () => void }) => (
                          <TypeAhead
                            searchProduct='hotels'
                            label={carLocationLabel}
                            placeholder={carLocationLabel}
                            searchKey='rcLocation'
                            errors={errors}
                            setValue={setValue}
                            disableShadowState={disableShadowState}
                            defaultSelectedItem={watchRcLocation ?? null}
                            ref={registerTabElement}
                            onItemSelect={() => {
                              if (isMobile && document.activeElement instanceof HTMLElement) {
                                document.activeElement.blur()
                              }
                            }}
                            focusNextElement={focusNextElement}
                            skipBottomPaddingForMobile
                          />
                        )}
                      </ShadowEffect>
                      <Flex flexWrap={['wrap', null, 'nowrap']} style={{ gap: '16px' }}>
                        <CalendarWrapperBox style={{ flex: '1 1 45%' }}>
                          <ShadowEffect enabled>
                            {({ disableShadowState }: { disableShadowState: () => void }) => (
                              <PopoverContainer
                                data-calendar
                                open={isRcPickupCalendarOpen}
                                onDismiss={() => {
                                  setIsRcPickupCalendarOpen(false)
                                  disableShadowState()
                                }}
                                domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                                skipBottomPaddingForMobile
                              >
                                <DateField
                                  name='rcPickupDate'
                                  label='Pick-up'
                                  index={3}
                                  ref={registerTabElement}
                                  value={formatDate({ dateStr: watchRcPickupDate ?? null })}
                                  onFocus={() => {
                                    setIsRcPickupCalendarOpen(true)
                                  }}
                                  error={(() => {
                                    if (errors.rcPickupDate) {
                                      return errors.rcPickupDate.message
                                    }
                                  })()}
                                  aria-expanded={isRcPickupCalendarOpen ? 'true' : 'false'}
                                />
                                <Calendar
                                  use2024DesignSeti
                                  isMobile={isMobile}
                                  roundedCorners
                                  monthWidthPx={350}
                                  monthsToShow={2}
                                  selectTwoDates={false}
                                  showHoveredDates={false}
                                  startDate={watchRcPickupDate}
                                  onChange={(_, { startDate }) => {
                                    if (startDate) {
                                      setIsRcPickupCalendarOpen(false)
                                      disableShadowState()
                                    }
                                    setValue('rcPickupDate', startDate ?? '', {
                                      shouldDirty: true,
                                    })
                                  }}
                                  useDefaultFooter
                                  onDismiss={() => {
                                    setIsRcPickupCalendarOpen(false)
                                    disableShadowState()
                                  }}
                                />
                              </PopoverContainer>
                            )}
                          </ShadowEffect>
                        </CalendarWrapperBox>
                        <Box style={{ flex: '1 1 45%' }}>
                          <ShadowEffect enabled>
                            <TimeField
                              id='rc-pickup-time'
                              name='rcPickupTime'
                              label='Pick-up Time'
                              style={{ height: '56px' }}
                              value={watchRcPickupTime}
                              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                setValue('rcPickupTime', e.currentTarget.value)
                                e.target.blur()
                              }}
                            />
                          </ShadowEffect>
                        </Box>
                        <CalendarWrapperBox style={{ flex: '1 1 45%' }}>
                          <ShadowEffect enabled>
                            {({ disableShadowState }: { disableShadowState: () => void }) => (
                              <PopoverContainer
                                data-calendar
                                open={isRcDropoffCalendarOpen}
                                onDismiss={() => {
                                  setIsRcDropoffCalendarOpen(false)
                                  disableShadowState()
                                }}
                                domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                                skipBottomPaddingForMobile
                              >
                                <DateField
                                  name='rcDropoffDate'
                                  label='Drop-off'
                                  index={4}
                                  ref={registerTabElement}
                                  value={formatDate({ dateStr: watchRcDropoffDate ?? null })}
                                  onFocus={() => {
                                    setIsRcDropoffCalendarOpen(true)
                                  }}
                                  error={(() => {
                                    if (errors.rcDropoffDate) {
                                      return errors.rcDropoffDate.message
                                    }
                                  })()}
                                  aria-expanded={isRcDropoffCalendarOpen ? 'true' : 'false'}
                                />
                                <Calendar
                                  use2024DesignSeti
                                  isMobile={isMobile}
                                  monthsToShow={2}
                                  roundedCorners
                                  monthWidthPx={350}
                                  selectTwoDates={false}
                                  showHoveredDates={false}
                                  minDate={getRcMinDate(watchRcPickupDate)}
                                  startDate={watchRcDropoffDate}
                                  onChange={(_, { startDate }) => {
                                    if (startDate) {
                                      setIsRcDropoffCalendarOpen(false)
                                      disableShadowState()
                                    }
                                    setValue('rcDropoffDate', startDate ?? '', {
                                      shouldDirty: true,
                                    })
                                  }}
                                  useDefaultFooter
                                  onDismiss={() => {
                                    setIsRcDropoffCalendarOpen(false)
                                    disableShadowState()
                                  }}
                                />
                              </PopoverContainer>
                            )}
                          </ShadowEffect>
                        </CalendarWrapperBox>
                        <Box style={{ flex: '1 1 45%' }}>
                          <ShadowEffect enabled>
                            <TimeField
                              id='rc-dropoff-time'
                              name='rcDropoffTime'
                              label='Drop-off Time'
                              style={{ height: '56px' }}
                              value={watchRcDropoffTime}
                              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                setValue('rcDropoffTime', e.currentTarget.value)
                                e.target.blur()
                              }}
                            />
                          </ShadowEffect>
                        </Box>
                      </Flex>
                    </>
                  )}
                </>
              )}
              {isMobile && <SearchFormButton style={{ flex: 'auto' }} buttonText={buttonText} />}
            </Flex>
          </>
        )
      }}
    </FormContainer>
  )
}

export default Form
